import React, { useState } from 'react';
import cc from 'classcat';
import Spinner from '@atlaskit/spinner';
import { captureException, setExtra } from '@sentry/core';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { Button } from '../../index';
import FormikField from '../FormikField';
import { Heading } from '../Wizard';
import { trackEvent } from '../../../lib/GA';
import FreeformHelper from '../../../lib/FreeformHelper';

import styles from './styles.module.scss';
import { useStore } from '../../../hooks';

interface CourseFormValues {
  fullName: string;
  phone: string;
  email: string;
  freeform_form_handle: string;
}

interface Props {
  course: string;
}

const CourseForm = ({ course }: Props) => {
  const globals = useStore('globals');
  const initialValues: CourseFormValues = {
    fullName: '',
    phone: '',
    email: '',
    freeform_form_handle: ''
  };

  const [submitted, setSubmitted] = useState(false);
  const [errored, setErrored] = useState(false);

  const formHandle = 'courseForm';
  const freeForm = globals?.freeform?.find((form) => form.handle === formHandle);

  async function handleSubmit(values: CourseFormValues, formikHelpers: FormikHelpers<CourseFormValues>) {
    const formData = new FormData();
    // istanbul ignore next
    Object.keys(values).forEach((key) => formData.append(key, values[key] == null ? '' : values[key]));
    formData.append('course', course);
    formData.append('handle', formHandle);

    const response = await FreeformHelper.sendForm(formData, freeForm);
    if (response.success) {
      setSubmitted(true);
      trackEvent('Course Form', 'Successful Submission', document.title);
    } else {
      setErrored(true);
      setExtra('errors', response.errors);
      captureException('Course Form submission failed');
    }

    formikHelpers.setSubmitting(false);
  }

  function renderConfirmation() {
    return (
      <div className={styles.message}>
        <Heading>Thank you for your enquiry!</Heading>
        <p>Thanks! Your enquiry has been received and someone from our team will be in touch with you shortly.</p>
      </div>
    );
  }

  function renderError() {
    return (
      <div className={styles.message}>
        <h3>Something went wrong</h3>
        <p>An error occured while trying to submit your enquiry.</p>
        <p>Please try again or call us below.</p>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={false}
      validationSchema={CourseForm.validationSchema}
      onSubmit={handleSubmit}
      validateOnChange
    >
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit} className={cc({ [styles.courseForm]: true, [styles.submitting]: isSubmitting })}>
          {submitted && renderConfirmation()}
          {errored && renderError()}
          {!submitted && !errored && (
            <>
              <Heading>Contact details</Heading>
              <div className="row mt-1">
                <div className="col-lg-6 mb-2 customInput">
                  <label htmlFor="fullName">Name</label>
                  <FormikField type="text" name="fullName" required placeholder="e.g. John" />
                </div>

                <div className="col-lg-6 mb-2 customInput">
                  <label htmlFor="phone">Phone</label>
                  <FormikField as="input" type="tel" name="phone" required placeholder="e.g. 0404 555 444" />
                </div>
              </div>

              <div className="row">
                <div className="col-12 mb-3 customInput">
                  <label htmlFor="email">Email</label>
                  <FormikField type="email" name="email" required placeholder="e.g. john@email.com" />
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  {
                    freeForm?.honeypot ? <input type={'hidden'} name={freeForm?.honeypot?.name || ''} value={freeForm.honeypot?.value || ''}/> : null
                  }

                  <Button as="submit" disabled={isSubmitting} type="orangeBorder" large>
                    Send email
                  </Button>
                </div>
              </div>
            </>
          )}

          <div className={cc({ [styles.spinner]: true, [styles.visible]: isSubmitting })}>
            <Spinner size="xlarge" isCompleting={!isSubmitting} />
          </div>
        </form>
      )}
    </Formik>
  );
};

CourseForm.validationSchema = Yup.object().shape({
  fullName: Yup.string().required(),
  phone: Yup.string().required(),
  email: Yup.string().email().required(),
});

export default CourseForm;
